import type { JSX } from 'react';

interface UiPathProps {
  /**
   * The click path, outermost first. The last entry is the control the reader
   * is looking for, and is the one rendered as the destination.
   */
  steps: [string, ...string[]];
  /** What they will see once they are there, when that is not obvious. */
  note?: string;
}

/**
 * Where a surface lives in the dashboard.
 *
 * Deliberately not a screenshot. A dashboard screenshot goes stale on the
 * next restyle, carries whichever workspace was open when it was taken, and
 * still leaves the reader to work out the route; the route is the part they
 * came for. Rendered as a trail so it reads at a glance in a wall of prose.
 */
export function UiPath({ steps, note }: UiPathProps): JSX.Element {
  return (
    <div
      className="dp-uipath"
      role="group"
      aria-label={`Where to find it: ${steps.join(' then ')}`}
    >
      <span className="dp-uipath-label">Where</span>
      <ol className="dp-uipath-trail">
        {steps.map((step, index) => (
          // The path is a fixed literal per call site, so the index is stable;
          // the label alone is not a key — "Subscriptions" can legitimately
          // appear twice in one route.
          <li className="dp-uipath-step" key={`${index}-${step}`}>
            {step}
          </li>
        ))}
      </ol>
      {note ? <p className="dp-uipath-note">{note}</p> : null}
    </div>
  );
}
